import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { getFullName } from "@/lib/userUtils";
import { toastError } from "@/lib/toast";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { useConfirm } from "@/components/ConfirmDialogProvider";

import { API_BASE_URL } from "@/lib/apiBase";
const InvitationsList = ({ orgId, initialType = "all", initialStatus = "pending", onInvitationUpdate }) => {
  const { accessToken, login, logout } = useAuth();
  const confirm = useConfirm();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [type, setType] = useState(initialType); // "sent", "received", "all"
  const [status, setStatus] = useState(initialStatus); // "pending", "accepted", "rejected"
  const [currentUser, setCurrentUser] = useState(null);

  // Helper function to make authenticated API calls with token refresh
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    try {
      return await requestFn(token);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (err.response.data.message === "Token has expired") {
          try {
            const refreshResponse = await axios.post(
              `${API_BASE_URL}/auth/refresh`,
              null,
              {
                withCredentials: true,
              }
            );
            const newAccessToken = refreshResponse.data.access_token || refreshResponse.data.accessToken;
            login(newAccessToken);
            return await requestFn(newAccessToken);
          } catch (refreshError) {
            console.error("Failed to refresh access token:", refreshError);
            logout();
            throw new Error("Session expired. Please log in again.");
          }
        }
      }
      throw err;
    }
  };

  // Fetch current user
  const fetchCurrentUser = async () => {
    if (!accessToken) return;
    try {
      const response = await makeAuthenticatedRequest(async (token) => {
        return await axios.get(`${API_BASE_URL}/user/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      });
      setCurrentUser(response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to fetch current user:", err);
      return null;
    }
  };

  // Fetch invitations
  const fetchInvitations = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (type !== "all") {
        params.type = type;
      }
      if (status) {
        params.status = status;
      }
      if (orgId) {
        params.org_id = orgId;
      }

      const response = await makeAuthenticatedRequest(async (token) => {
        return await axios.get(`${API_BASE_URL}/invitations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        });
      });

      setInvitations(response.data || []);
    } catch (err) {
      console.error("Failed to fetch invitations:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch invitations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when auth token changes
  }, [accessToken]);

  useEffect(() => {
    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when filters/auth change
  }, [accessToken, type, status, orgId]);

  // Handle accept invitation
  const handleAccept = async (invitationId) => {
    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/invitations/${invitationId}/accept`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      });

      // Refresh invitations list
      await fetchInvitations();
      // Call update callback if provided
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
    } catch (err) {
      console.error("Failed to accept invitation:", err);
      toastEntitlementOrError(err, "Failed to accept invitation", {
        orgId: null,
        canManageSettings: false,
      });
    }
  };

  // Handle reject invitation
  const handleReject = async (invitationId) => {
    const ok = await confirm({
      title: "Reject invitation?",
      description: "Are you sure you want to reject this invitation?",
      confirmLabel: "Reject",
      variant: "destructive",
    });
    if (!ok) return;

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.post(
          `${API_BASE_URL}/invitations/${invitationId}/reject`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      });

      // Refresh invitations list
      await fetchInvitations();
      // Call update callback if provided
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
    } catch (err) {
      console.error("Failed to reject invitation:", err);
      toastError(err, "Failed to reject invitation");
    }
  };

  // Handle cancel invitation
  const handleCancel = async (invitationId) => {
    const ok = await confirm({
      title: "Cancel invitation?",
      description: "Are you sure you want to cancel this invitation?",
      confirmLabel: "Cancel invitation",
      variant: "destructive",
    });
    if (!ok) return;

    try {
      await makeAuthenticatedRequest(async (token) => {
        const response = await axios.delete(
          `${API_BASE_URL}/invitations/${invitationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      });

      // Refresh invitations list
      await fetchInvitations();
      // Call update callback if provided
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
    } catch (err) {
      console.error("Failed to cancel invitation:", err);
      toastError(err, "Failed to cancel invitation");
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "pending":
        return "default";
      case "accepted":
        return "secondary";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={type} onValueChange={setType}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="received">Received</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
        </TabsList>

        {/* Status filter - only show for pending when viewing all or received */}
        {(type === "all" || type === "received") && (
          <div className="flex gap-2 mt-4">
            <Button
              variant={status === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatus("pending")}
            >
              Pending
            </Button>
            <Button
              variant={status === "accepted" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatus("accepted")}
            >
              Accepted
            </Button>
            <Button
              variant={status === "rejected" ? "default" : "outline"}
              size="sm"
              onClick={() => setStatus("rejected")}
            >
              Rejected
            </Button>
          </div>
        )}

        <TabsContent value={type} className="mt-4">
          {invitations.length === 0 ? (
            <p className="text-muted-foreground">No invitations found.</p>
          ) : (
            <div className="space-y-4">
              {invitations.map((invitation) => {
                // Determine if invitation is received or sent based on current user
                const invitationId = invitation.invitationId || invitation.invitation_id;
                const inviterId = invitation.inviterId || invitation.inviter_id || invitation.inviter?.user_id;
                const inviteeId = invitation.inviteeId || invitation.invitee_id || invitation.invitee?.user_id;
                const currentUserId = currentUser?.user_id;
                const kind = invitation.kind || "invite";
                const isJoinRequest = kind === "join_request";
                const canApproveJoin =
                  Boolean(invitation.can_approve_join_request) ||
                  Boolean(invitation.canApproveJoinRequest);

                const isReceived = currentUserId && inviteeId === currentUserId && !isJoinRequest;
                const isPending = invitation.status === "pending";
                const isOwnJoinRequest =
                  isJoinRequest &&
                  currentUserId &&
                  (inviterId === currentUserId || inviteeId === currentUserId) &&
                  !canApproveJoin;

                return (
                  <Card key={invitation.invitationId || invitation.invitation_id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">
                            {isJoinRequest ? (
                              <>
                                Join request: {invitation.team?.name || "team"}
                              </>
                            ) : invitation.team ? (
                              <>Invitation to team: {invitation.team.name}</>
                            ) : (
                              <>Invitation to organization: {invitation.org?.name || "Unknown Org"}</>
                            )}
                          </CardTitle>
                          <CardDescription className="mt-2">
                            {isJoinRequest ? (
                              <>
                                Requested by:{" "}
                                {invitation.invitee
                                  ? getFullName(invitation.invitee)
                                  : invitation.email || invitation.inviteeId || "Unknown"}
                              </>
                            ) : isReceived ? (
                              <>
                                Invited by: {invitation.inviter ? getFullName(invitation.inviter) : invitation.inviterId || "Unknown"}
                              </>
                            ) : (
                              <>
                                Invited: {invitation.invitee ? getFullName(invitation.invitee) : invitation.email || invitation.inviteeId || "Unknown"}
                              </>
                            )}
                          </CardDescription>
                        </div>
                        <Badge variant={getStatusBadgeVariant(invitation.status)}>
                          {isJoinRequest && isPending ? "join request" : invitation.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        {invitation.role && (
                          <div>
                            <span className="font-medium">Role: </span>
                            <span className="text-muted-foreground">{invitation.role}</span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Date: </span>
                          <span className="text-muted-foreground">
                            {new Date(invitation.createdAt || invitation.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      {isPending && (
                        <div className="flex gap-2 mt-4">
                          {canApproveJoin ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleAccept(invitationId)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReject(invitationId)}
                              >
                                Deny
                              </Button>
                            </>
                          ) : isReceived ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleAccept(invitationId)}
                              >
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReject(invitationId)}
                              >
                                Reject
                              </Button>
                            </>
                          ) : isOwnJoinRequest ||
                            (currentUserId && inviterId === currentUserId) ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancel(invitationId)}
                            >
                              Cancel
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvitationsList;

